import React, { useState, useEffect } from 'react';
import { LanguageToggle } from './LanguageToggle';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { CheckCheck, User, LogOut, LayoutDashboard, Bell, Zap, MessageSquare, FolderOpen } from 'lucide-react';
import axios from 'axios';
import { getFlags } from '../lib/flags';
import { useUnreadCount, setUnread } from '../lib/unreadStore';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple in-memory cache to avoid redundant API calls during SPA navigation
let _headerCache = { data: null, timestamp: 0 };
const CACHE_TTL = 60000; // 1 minute

export const Header = () => {
  const navigate = useNavigate();
  const { clearResults } = useApp();
  const { user, token, isAuthenticated, logout, sessionExpired, dismissSessionExpired } = useAuth();
  const { lang } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const unreadCount = useUnreadCount();
  const [paidCredits, setPaidCredits] = useState(0);
  const [subscriptionActive, setSubscriptionActive] = useState(false);
  const [premiumEnabled, setPremiumEnabled] = useState(false);

  useEffect(() => {
    getFlags().then((f) => setPremiumEnabled(!!f.premium_enabled));
  }, []);

  // Show session expired banner, then redirect to login
  useEffect(() => {
    if (sessionExpired) {
      const timer = setTimeout(() => {
        dismissSessionExpired();
        navigate('/login');
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [sessionExpired, dismissSessionExpired, navigate]);

  useEffect(() => {
    if (!isAuthenticated || !token) return;
    const now = Date.now();
    if (_headerCache.data && now - _headerCache.timestamp < CACHE_TTL) {
      setPaidCredits(_headerCache.data.credits || 0);
      setSubscriptionActive(_headerCache.data.sub || false);
      return;
    }
    const fetchData = async () => {
      try {
        const [notifRes, usageRes] = await Promise.all([
          axios.get(`${API_URL}/monitoring/notifications`, { params: { unread_only: true }, headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
          axios.get(`${API_URL}/user/usage`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
        ]);
        const unread = notifRes.data.unread_count || 0;
        const credits = usageRes.data.paid_credits || 0;
        const sub = usageRes.data.subscription_active || false;
        setUnread(unread);
        setPaidCredits(credits);
        setSubscriptionActive(sub);
        _headerCache = { data: { credits, sub }, timestamp: Date.now() };
      } catch {}
    };
    fetchData();
    const interval = setInterval(fetchData, 120000);
    return () => clearInterval(interval);
  }, [isAuthenticated, token]);

  const handleLogoClick = () => {
    clearResults();
    navigate('/');
  };

  const handleLogout = () => {
    logout();
    setMenuOpen(false);
    navigate('/');
  };

  return (
    <>
      {sessionExpired && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-[#DC2626] text-white text-center py-3 px-4 text-sm font-medium animate-fade-in-up" data-testid="session-expired-banner">
          {lang === 'fr'
            ? 'Vous avez \u00e9t\u00e9 d\u00e9connect\u00e9 car une connexion a \u00e9t\u00e9 effectu\u00e9e depuis un autre appareil.'
            : 'U bent uitgelogd omdat er is ingelogd vanaf een ander apparaat.'}
        </div>
      )}
    <header className="fixed top-0 left-0 right-0 z-50 glass safe-area-top">
      <div className="max-w-2xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between min-w-0">
        <button
          onClick={handleLogoClick}
          className="flex items-center gap-1.5 sm:gap-2 hover:opacity-80 transition-opacity min-w-0 shrink-0"
          data-testid="logo-button"
        >
          <div className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-[#102A43] shrink-0">
            <CheckCheck className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </div>
          <span className="font-bold text-[15px] sm:text-lg tracking-tight font-['Outfit'] text-[#102A43]">
            checkcheck<span className="text-[#2563EB]">.be</span>
          </span>
        </button>

        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <LanguageToggle />

          {/* Pro badge for subscribers only */}
          {isAuthenticated && subscriptionActive && premiumEnabled && (
            <div className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#2563EB]/10 border border-[#2563EB]/20 text-[#2563EB]" data-testid="pro-badge">
              <Zap className="w-3 h-3" />
              <span>Pro</span>
            </div>
          )}

          {isAuthenticated ? (
            <div className="relative">
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-full bg-slate-100 border border-slate-200 hover:bg-slate-100 transition-all"
                data-testid="user-menu-button"
              >
                <User className="w-4 h-4 text-[#2563EB]" />
                <span className="text-sm text-slate-700 max-w-[60px] sm:max-w-[80px] truncate hidden sm:inline">{user?.name}</span>
                {unreadCount > 0 && (
                  <span className="w-4 h-4 bg-[#DC2626] text-white text-[10px] rounded-full flex items-center justify-center" data-testid="unread-badge">
                    {unreadCount}
                  </span>
                )}
              </button>

              {menuOpen && (
                <>
                  <div className="fixed inset-0" onClick={() => setMenuOpen(false)} style={{ zIndex: 40 }} />
                  <div className="absolute right-0 top-full mt-2 w-52 bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden" style={{ zIndex: 50 }} data-testid="user-dropdown">
                    <button
                      onClick={() => { setMenuOpen(false); navigate('/dashboard'); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
                      data-testid="menu-dashboard"
                    >
                      <LayoutDashboard className="w-4 h-4 text-[#2563EB]" />
                      {lang === 'fr' ? 'Mon CheckCheck' : 'Mijn CheckCheck'}
                      {unreadCount > 0 && (
                        <span className="ml-auto w-5 h-5 bg-[#DC2626] text-white text-[10px] rounded-full flex items-center justify-center">
                          {unreadCount}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => { setMenuOpen(false); navigate('/folders'); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
                      data-testid="menu-folders"
                    >
                      <FolderOpen className="w-4 h-4 text-[#2563EB]" />
                      {lang === 'fr' ? 'Mes dossiers' : 'Mijn dossiers'}
                    </button>
                    <button
                      onClick={() => { setMenuOpen(false); navigate('/contact'); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-600 hover:bg-slate-100 hover:text-[#172033] transition-colors"
                      data-testid="menu-contact"
                    >
                      <MessageSquare className="w-4 h-4 text-[#2563EB]" />
                      {lang === 'fr' ? 'Contact' : 'Contact'}
                    </button>
                    <div className="border-t border-slate-100" />
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-600 hover:bg-slate-100 hover:text-[#DC2626] transition-colors"
                      data-testid="menu-logout"
                    >
                      <LogOut className="w-4 h-4" />
                      {lang === 'fr' ? 'Déconnexion' : 'Uitloggen'}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <button
              onClick={() => navigate('/login')}
              className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-full bg-[#2563EB]/10 border border-[#2563EB]/20 hover:bg-[#2563EB]/20 transition-all"
              data-testid="login-button"
            >
              <User className="w-4 h-4 text-[#2563EB]" />
              <span className="text-sm text-[#2563EB] hidden sm:inline">{lang === 'fr' ? 'Connexion' : 'Inloggen'}</span>
            </button>
          )}
        </div>
      </div>
    </header>
    </>
  );
};
